import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { format, addDays, startOfDay } from 'date-fns';

const ScheduleInspection = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [inspection, setInspection] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [loading, setLoading] = useState(true);
  const [scheduling, setScheduling] = useState(false);
  const [error, setError] = useState('');

  const fetchInspection = useCallback(async () => {
    try {
      const response = await api.get(`/inspections/customer/${id}`);
      setInspection(response.data.data);
    } catch (error) {
      console.error('Error fetching inspection:', error);
      setError('Failed to load inspection details');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchInspection();
  }, [fetchInspection]);

  useEffect(() => {
    if (selectedDate) {
      fetchAvailableSlots(selectedDate);
    }
  }, [selectedDate]);

  const fetchAvailableSlots = async (date) => {
    try {
      const response = await api.get('/inspections/slots', {
        params: { date }
      });
      setAvailableSlots(response.data.data.availableSlots);
      setSelectedTime(''); // Reset time selection when date changes
    } catch (error) {
      console.error('Error fetching available slots:', error);
      setError('Failed to load available time slots');
    }
  };

  const generateDateOptions = () => {
    const dates = [];
    const today = startOfDay(new Date());

    // Generate next 30 days
    for (let i = 1; i <= 30; i++) {
      const date = addDays(today, i);
      dates.push({
        value: format(date, 'yyyy-MM-dd'),
        label: format(date, 'EEEE, MMMM d, yyyy')
      });
    }

    return dates;
  };

  const handleSchedule = async () => {
    if (!selectedDate || !selectedTime) {
      setError('Please select both date and time');
      return;
    }

    try {
      setScheduling(true);
      setError('');

      await api.put(`/inspections/customer/${id}/schedule`, {
        scheduledDate: selectedDate,
        scheduledTime: selectedTime
      });

      // Redirect to inspection details
      navigate(`/dashboard/inspections/${id}`, {
        state: { message: 'Inspection scheduled successfully!' }
      });
    } catch (error) {
      console.error('Error scheduling inspection:', error);
      setError(error.response?.data?.message || 'Failed to schedule inspection');
    } finally {
      setScheduling(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!inspection) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900">Inspection Not Found</h1>
          <p className="mt-2 text-gray-600">The inspection you're looking for doesn't exist or you don't have access to it.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Schedule Inspection</h1>
        <p className="mt-2 text-gray-600">
          Choose your preferred date and time for the {inspection.inspectionMonth} inspection
        </p>
      </div>

      {/* Inspection Details */}
      <div className="bg-white shadow rounded-lg p-6 mb-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Inspection Details</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <p className="text-sm text-gray-500">Month</p>
            <p className="text-sm font-medium text-gray-900">{inspection.inspectionMonth}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Property</p>
            <p className="text-sm font-medium text-gray-900">{inspection.propertyAddress}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Property Type</p>
            <p className="text-sm font-medium text-gray-900">{inspection.propertyType}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Status</p>
            <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
              Pending Schedule
            </span>
          </div>
        </div>
      </div>

      {/* Scheduling Form */}
      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Select Date & Time</h2>

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-4">
            <div className="text-red-800">{error}</div>
          </div>
        )}

        {/* Date Selection */}
        <div className="mb-6">
          <label htmlFor="date" className="block text-sm font-medium text-gray-700 mb-2">
            Preferred Date
          </label>
          <select
            id="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
          >
            <option value="">Select a date</option>
            {generateDateOptions().map((date) => (
              <option key={date.value} value={date.value}>
                {date.label}
              </option>
            ))}
          </select>
        </div>

        {/* Time Selection */}
        {selectedDate && (
          <div className="mb-6">
            <label htmlFor="time" className="block text-sm font-medium text-gray-700 mb-2">
              Available Time Slots
            </label>
            {availableSlots.length === 0 ? (
              <p className="text-gray-500 text-sm">No time slots available for this date. Please select another date.</p>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                {availableSlots.map((slot) => (
                  <button
                    key={slot}
                    onClick={() => setSelectedTime(slot)}
                    className={`p-3 text-sm font-medium rounded-md border ${
                      selectedTime === slot
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                    }`}
                  >
                    {slot}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Selected Date/Time Summary */}
        {selectedDate && selectedTime && (
          <div className="mb-6 bg-blue-50 border border-blue-200 rounded-md p-4">
            <h3 className="text-sm font-medium text-blue-800 mb-2">Selected Schedule</h3>
            <p className="text-sm text-blue-700">
              <strong>Date:</strong> {format(new Date(selectedDate), 'EEEE, MMMM d, yyyy')}<br />
              <strong>Time:</strong> {selectedTime}
            </p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-end space-x-3">
          <button
            onClick={() => navigate('/dashboard/inspections')}
            className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Cancel
          </button>
          <button
            onClick={handleSchedule}
            disabled={!selectedDate || !selectedTime || scheduling}
            className="bg-blue-600 py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {scheduling ? 'Scheduling...' : 'Schedule Inspection'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ScheduleInspection;